import React, { useMemo } from "react";
import { useAllRoyalties, useAllRoyaltiesByYear } from "@seabrookstudios/pitch2table";
import { idExtractor, useMakeListAndDetail, useMakeListBuilder, useMakeListView } from "../core/useMakeLayout";
import { NoRoyalties } from "./NoRoyalties";
import { RoyaltyTotals } from "./RoyaltyTotals";
import { useTypeFromParam, useYearFromParam } from "./params";
import { RoyaltyFromRouteSansGame } from "../games/royalties/RoyaltyFromRoute";
import {
  useAllRoyaltiesByGame,
  useAllRoyaltiesByPublisher,
  useAllRoyaltiesByType,
  useAllRoyaltiesForPublisher,
  useAllRoyaltiesForType,
  useAllRoyaltiesForYear,
  useAllRoyaltiesWithoutContract,
  useAllRoyaltiesWithoutEdition,
  useAllRoyaltiesWithoutUnits,
} from "@seabrookstudios/pitch2table";
import {
  ListEntry,
  ListEntryByPublisher,
  ListEntryByType,
  ListEntryWithGameInfo,
  RoyaltiesByGameListEntry,
  RoyaltiesByPublisherListEntry,
  RoyaltiesByTypeListEntry,
  RoyaltiesByYearListEntry,
} from "./ListEntry";
import { usePublisherIdFromRoute } from "../games/WithGameIdFromRoute";
import { usePublisherNameOrUnlinked } from "../publisher/queries";
import { RoyaltyTypeLabels } from "@seabrookstudios/pitch2table-core";
import {
  RoyaltiesByPublisherParetoChart,
  RoyaltiesByYearForPublisherChart,
  RoyaltyByTypeChart,
  RoyaltyByYearChart,
  RoyaltiesByYearParetoChart,
} from "./Chart";
import { Path } from "../Path";

/**
 * Custom hook that returns only unpaid royalties
 */
export const useUnpaidRoyalties = () => {
  const { data: royalties = [], ...rest } = useAllRoyalties();
  const unpaidRoyalties = useMemo(() => royalties.filter((royalty) => !royalty.paid), [royalties]);

  return {
    data: unpaidRoyalties,
    ...rest,
  };
};

export const RoyaltiesByType = () => {
  const useWrappedQuery = () => useAllRoyaltiesByType();

  const useMakeList = useMakeListBuilder(
    RoyaltiesByTypeListEntry,
    (item) => item[0].type,
    "Royalties by Type",
    NoRoyalties,
    undefined,
    RoyaltyByTypeChart,
    Path.royalties().none()
  );

  const List = useMakeListView(useWrappedQuery, [], "all-royalties", useMakeList);

  return <List />;
};

export const RoyaltiesByPublisher = () => {
  const useWrappedQuery = () => useAllRoyaltiesByPublisher();

  const useMakeList = useMakeListBuilder(
    RoyaltiesByPublisherListEntry,
    (item) => item[0].publisherId,
    "Royalties by Publisher",
    NoRoyalties,
    undefined,
    RoyaltiesByPublisherParetoChart,
    Path.royalties().none()
  );

  const List = useMakeListView(useWrappedQuery, [], "all-royalties", useMakeList);

  return <List />;
};

export const RoyaltiesByGameId = () => {
  const useWrappedQuery = () => useAllRoyaltiesByGame();

  const useMakeList = useMakeListBuilder(
    RoyaltiesByGameListEntry,
    (item) => item[0].gameId,
    "Royalties by Game",
    NoRoyalties,
    undefined,
    undefined,
    Path.royalties().none()
  );

  const List = useMakeListView(useWrappedQuery, [], "all-royalties", useMakeList);

  return <List />;
};

const keyExtractor = (item) => item[0].id;

export const RoyaltiesByYear = () => {
  const useWrappedQuery = () => useAllRoyaltiesByYear();

  const useMakeList = useMakeListBuilder(
    RoyaltiesByYearListEntry,
    keyExtractor,
    "Royalties by Year",
    NoRoyalties,
    undefined,
    RoyaltyByYearChart,
    Path.royalties().none()
  );

  const filters = useMemo(() => [], []);
  const List = useMakeListView(useWrappedQuery, filters, "all-royalties", useMakeList);

  return <List />;
};

export const RoyaltiesListAndDetail = () => {
  const { data: royalties = [] } = useAllRoyalties();

  const useWrappedQuery = () => useAllRoyalties();

  const useMakeList = useMakeListBuilder(
    ListEntry,
    idExtractor,
    "All Royalties",
    NoRoyalties,
    undefined,
    () => <RoyaltyTotals royalties={royalties} />,
    Path.royalties().none()
  );

  const List = useMakeListView(useWrappedQuery, [], "all-royalties", useMakeList);

  return <List />;
};

export const RoyaltiesUnpaidListAndDetail = () => {
  const { data: unpaidRoyalties = [] } = useUnpaidRoyalties();

  const useWrappedQuery = () => useUnpaidRoyalties();

  const useMakeList = useMakeListBuilder(
    ListEntry,
    idExtractor,
    "Unpaid Royalties",
    NoRoyalties,
    undefined,
    () => <RoyaltyTotals royalties={unpaidRoyalties} hideTotal hideUnits />,
    Path.royalties().none()
  );

  const List = useMakeListView(useWrappedQuery, [], "unpaid-royalties", useMakeList);

  return <List />;
};

export const RoyaltiesWithoutUnitsListAndDetail = () => {
  const { data: royalties = [] } = useAllRoyaltiesWithoutUnits();

  const useWrappedQuery = () => useAllRoyaltiesWithoutUnits();

  const useMakeList = useMakeListBuilder(
    ListEntry,
    idExtractor,
    "Royalties Without Units",
    NoRoyalties,
    undefined,
    () => <RoyaltyTotals royalties={royalties} hideUnits />,
    Path.royalties().none()
  );

  const List = useMakeListView(useWrappedQuery, [], "royalties-without-units", useMakeList);

  return <List />;
};

export const RoyaltiesWithoutContractListAndDetail = () => {
  const { data: royalties = [] } = useAllRoyaltiesWithoutContract();

  const useWrappedQuery = () => useAllRoyaltiesWithoutContract();

  const useMakeList = useMakeListBuilder(
    ListEntry,
    idExtractor,
    "Royalties Without Contract",
    NoRoyalties,
    undefined,
    () => <RoyaltyTotals royalties={royalties} />,
    Path.royalties().none()
  );

  const List = useMakeListView(useWrappedQuery, [], "royalties-without-contract", useMakeList);

  return <List />;
};

export const RoyaltiesWithoutEditionListAndDetail = () => {
  const { data: royalties = [] } = useAllRoyaltiesWithoutEdition();

  const useWrappedQuery = () => useAllRoyaltiesWithoutEdition();

  const useMakeList = useMakeListBuilder(
    ListEntry,
    idExtractor,
    "Royalties Without Edition",
    NoRoyalties,
    undefined,
    () => <RoyaltyTotals royalties={royalties} />,
    Path.royalties().none()
  );

  const List = useMakeListView(useWrappedQuery, [], "royalties-without-edition", useMakeList);

  return <List />;
};

const RoyaltyListForYearHeader = ({ royalties }) => (
  <>
    <RoyaltiesByYearParetoChart />
    <RoyaltyTotals royalties={royalties} />
  </>
);

export const RoyaltyListForYear = () => {
  const year = useYearFromParam();
  const { data: royalties = [] } = useAllRoyaltiesForYear(year);

  const useWrappedQuery = () => useAllRoyaltiesForYear(year);

  const useMakeList = useMakeListBuilder(
    ListEntryWithGameInfo,
    idExtractor,
    year || "",
    NoRoyalties,
    undefined,
    () => <RoyaltyListForYearHeader royalties={royalties} />,
    Path.royalties().category("by-year")
  );

  const ListAndDetail = useMakeListAndDetail(
    useWrappedQuery,
    [],
    "all-royalties",
    useMakeList,
    RoyaltyFromRouteSansGame
  );
  return <ListAndDetail />;
};

export const RoyaltyListForType = () => {
  const type = useTypeFromParam();
  const { data: royalties = [] } = useAllRoyaltiesForType(type);

  const useWrappedQuery = () => useAllRoyaltiesForType(type);

  const useMakeList = useMakeListBuilder(
    ListEntryByType,
    idExtractor,
    type ? RoyaltyTypeLabels[type] : "Unknown Type",
    NoRoyalties,
    undefined,
    () => <RoyaltyTotals royalties={royalties} />,
    Path.royalties().category("by-type")
  );

  const ListAndDetail = useMakeListAndDetail(
    useWrappedQuery,
    [],
    "all-royalties",
    useMakeList,
    RoyaltyFromRouteSansGame
  );
  return <ListAndDetail />;
};

const RoyaltyListForPublisherHeader = ({ publisherId, royalties }) => (
  <>
    <RoyaltiesByYearForPublisherChart publisherId={publisherId} />
    <RoyaltyTotals royalties={royalties} />
  </>
);

export const RoyaltyListForPublisher = () => {
  const publisherId = usePublisherIdFromRoute();
  const { data: royalties = [] } = useAllRoyaltiesForPublisher(publisherId);

  const useWrappedQuery = () => useAllRoyaltiesForPublisher(publisherId);
  const publisherName = usePublisherNameOrUnlinked(publisherId);

  const useMakeList = useMakeListBuilder(
    ListEntryByPublisher,
    idExtractor,
    publisherName,
    NoRoyalties,
    undefined,
    () => <RoyaltyListForPublisherHeader publisherId={publisherId} royalties={royalties} />,
    Path.royalties().category("by-publisher")
  );

  const ListAndDetail = useMakeListAndDetail(
    useWrappedQuery,
    [],
    "all-royalties",
    useMakeList,
    RoyaltyFromRouteSansGame
  );
  return <ListAndDetail />;
};
